import { EmployeeLiteModel, useGetEmployees } from 'api-hooks/employee';
import { RoleLiteModel } from 'api-hooks/role';
import { NavigationRoutes } from 'common/constants';
import filterReduce from 'common/helpers/filter-reduce';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import {
  Container,
  SeparatorVertical,
  TopContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import EmployeeTable from './components/table';

export default function EmployeeList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<EmployeeLiteModel>[]>(
    () => [
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 150,
        sortName: 'name',
      },
      {
        header: t('common:role'),
        accessorKey: 'roles',
        minSize: 150,
        cell: ({ getValue }) => {
          return (
            <>
              {((getValue() as RoleLiteModel[]) || [])
                .map((item) => item.name)
                .join(', ')}
            </>
          );
        },
      },
      {
        header: t('common:address'),
        accessorKey: 'address',
        minSize: 180,
        sortName: 'address',
      },
      {
        header: t('common:username'),
        accessorKey: 'username',
        minSize: 180,
      },
      {
        header: t('common:phone'),
        accessorKey: 'phone',
        minSize: 150,
      },
      {
        header: t('cruise:cellphone_number'),
        accessorKey: 'phone_2',
        minSize: 150,
      },
      {
        header: t('common:job_level'),
        accessorKey: 'jobLevel',
        sortName: 'job_level',
        minSize: 120,
        cell({ row }) {
          const jobLevel = row.original.jobLevel;
          if (jobLevel) {
            return t(`enum:${jobLevel}`);
          }
          return '-';
        },
      },
      {
        header: t('common:join_date'),
        accessorKey: 'joinedAt',
        sortName: 'joined_at',
        minSize: 120,
        cell({ row }) {
          const joinedAt = row.original.joinedAt;
          if (joinedAt) {
            return formatDate(joinedAt, 'dd MMM yyyy');
          }
          return '-';
        },
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetEmployees,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const _filter = filterReduce(filters);

  const permission = useGetCrudPermission('employees');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:employee')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.EmployeesCreate}
        />
        <SeparatorVertical $margin={8} />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <EmployeeTable
          employees={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          filter={_filter}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
